import React, { useRef, useEffect, useMemo } from "react";
import { useSharedTranslation } from "@/shared/contexts/TranslateContext";
import { useDispatch, useSelector } from "react-redux";

import IconButtonToggleSetting from "../IconButton/IconButtonToggleSetting";
import IconButton from "../IconButton/IconButton";

import useToggleInLibrary from "../../hooks/useToggleInLibrary";

import { _sharedActionsSystem } from "../../store/system/actions";
import {
  selectorHistoryAllHistory,
  selectorHistoryAllHistoryUserId
} from "../../store/library/selectors";

import EgwWebFont from "../../assets/EgwWebFont";
import { SETTING } from "../../utils/settings/constants";
import { logOtherEvent, OtherLogEvents } from "../../api/analitics";
import { getBookIdNumber, getBookIdWithPrefix } from "../../utils/content/content";
import { selectorShopBooks } from "../../store/shop/selectors";
import { DATA_ATTR_HIDE_POPUP_ON_CLICK } from "../Popup/popup.constants";

import styles from "./IconButtonsSettings.module.scss";
import { ReaderFontSizes } from "../../utils/theme/theme";
import { settingsActions } from "../../../main/src/redux/settings/actions";
import { selectorSetting } from "../../store/settings/selectors";
import { clutch } from "../../utils/number/number";
import { selectorSystemIsLogin, selectorUserInfo } from "@/shared/store/system/selectors";

export const IconButtonSettingSyncLinks = ({ disabled }) => {
  return (
    <IconButtonToggleSetting
      setting={SETTING.syncReader}
      icon={EgwWebFont.sync}
      titleEnable="enableSync"
      titleDisable="disableSync"
      disabled={disabled}
    />
  );
};

export const IconButtonSettingSyncTranslations = ({ disabled }) => {
  return (
    <IconButtonToggleSetting
      setting={SETTING.showTranslations}
      icon={EgwWebFont.translation}
      titleEnable="Show translations"
      titleDisable="Hide translations"
      disabled={disabled}
    />
  );
};

export const IconButtonSettingSyncReaderAndSearch = ({ disabled }) => {
  return (
    <IconButtonToggleSetting
      setting={SETTING.syncReaderAndSearch}
      icon={EgwWebFont["sync-with-feature-search"]}
      titleEnable="syncFeaturedSearchEnable"
      titleDisable="syncFeaturedSearchDisable"
      disabled={disabled}
    />
  );
};

export const IconButtonSettingSyncPlayerAndReader = ({
  disabled, onContextMenu,
}) => {
  return (
    <IconButtonToggleSetting
      setting={SETTING.syncPlayerAndReader}
      icon={EgwWebFont["sync-with-player"]}
      titleEnable="enableSyncR&P"
      titleDisable="disableSyncR&P"
      disabled={disabled}
      onContextMenu={onContextMenu}
    />
  );
};

export const IconButtonSettingShowRefCodes = ({ disabled }) => {
  return (
    <IconButtonToggleSetting
      setting={SETTING.showRefCodes}
      icon={EgwWebFont.bracers}
      titleEnable="Show references"
      titleDisable="Hide references"
      disabled={disabled}
    />
  );
};

export const IconButtonSettingShowHeaders = ({ disabled }) => {
  return (
    <IconButtonToggleSetting
      setting={SETTING.showHeaders}
      icon={EgwWebFont["show-headers"]}
      titleEnable="Show headers"
      titleDisable="Hide headers"
      disabled={disabled}
    />
  );
};

export const IconButtonSettingPageNumbers = ({ disabled }) => {
  return (
    <IconButtonToggleSetting
      setting={SETTING.showPageNumbers}
      icon={EgwWebFont["page-numbers"]}
      titleEnable="Show page numbers"
      titleDisable="Hide page numbers"
      disabled={disabled}
    />
  );
};

export const IconButtonSettingShowPageBreaks = ({ disabled }) => {
  return (
    <IconButtonToggleSetting
      setting={SETTING.showPageBreaks}
      icon={EgwWebFont["page-breaks"]}
      titleEnable="Show page breaks"
      titleDisable="Hide page breaks"
      disabled={disabled}
    />
  );
};

export const IconButtonSettingShowSCEntriesInReader = ({ disabled }) => {
  return (
    <IconButtonToggleSetting
      setting={SETTING.showSCEntriesInReader}
      icon={EgwWebFont["paper-study-center"]}
      titleEnable="Show Study Center elements"
      titleDisable="Hide Study Center elements"
      disabled={disabled}
    />
  );
};

export const IconButtonSettingChangeReaderFontSize = ({
  isIncrease, onClick
}) => {
  const { t } = useSharedTranslation();
  const dispatch = useDispatch();

  const value = useSelector(selectorSetting(SETTING.readerFontSize.id));

  const handleFontSizeChange = () => {
    const valueDeffer = value || ReaderFontSizes.default;

    const { factor, min, max } = ReaderFontSizes;

    const valueNew = isIncrease ? valueDeffer + factor : valueDeffer - factor;

    dispatch(
      settingsActions.updateSettings(SETTING.readerFontSize.id, clutch(valueNew, min, max))
    );

    logOtherEvent(OtherLogEvents.READER_ACTION, {
      id: SETTING.readerFontSize.id,
      value: valueNew,
    });
  };

  return (
    <IconButton
      icon={isIncrease ? EgwWebFont["text-increase"] : EgwWebFont["text-decrease"]}
      title={t(isIncrease ? "Larger font" : "Smaller font")}
      onClick={onClick ?? handleFontSizeChange}
    />
  );
};

const PROPS_CONTROL_BTN_HIDE_POPUP_ON_CLICK = {
  [DATA_ATTR_HIDE_POPUP_ON_CLICK]: true,
};

export const IconButtonSettingToggleInML = ({
  isVariantBookmark,
  className,
  bordered,
  book,
  onToggleInMLSuccess,
  color,
}) => {
  const bookIdNumber = book ? getBookIdNumber(book.id) : undefined;

  const { t } = useSharedTranslation();
  const dispatch = useDispatch();

  const toggleInLibrary = useToggleInLibrary();

  const shopBooks = useSelector(selectorShopBooks);
  const allHistory = useSelector(selectorHistoryAllHistory);
  const allHistoryUserId = useSelector(selectorHistoryAllHistoryUserId);
  const userInfo = useSelector(selectorUserInfo);
  const userId = userInfo?.id;

  const isBookInLibrary = useMemo(() => {
    const bookId = getBookIdWithPrefix(bookIdNumber);
    const book = allHistory.find((item) => item.id === bookId);
    return !!book?.inLibrary;
  }, [allHistory, bookIdNumber]);

  const shopBook = shopBooks[bookIdNumber];

  const toggleInML = () => {
    return toggleInLibrary(isBookInLibrary, bookIdNumber, onToggleInMLSuccess ? () => {
      if (onToggleInMLSuccess) {
        onToggleInMLSuccess(!isBookInLibrary);
      }
    } : undefined);
  };

  // ======== Add to ML on became login if pressed add to ML ==========
  const refIsAddBookToMLOnBecameAuthorized = useRef(false);

  const isLogin = useSelector(selectorSystemIsLogin);

  const isAllHistoryRelatedToCurrentUser = allHistoryUserId === userId;
  useEffect(() => {
    if (
      isAllHistoryRelatedToCurrentUser
      && isLogin
      && refIsAddBookToMLOnBecameAuthorized.current
    ) {
      if (!isBookInLibrary) {
        toggleInML();
      }

      refIsAddBookToMLOnBecameAuthorized.current = false;
    }
  }, [isLogin, isAllHistoryRelatedToCurrentUser]);
  // ================================================


  return (
    <IconButton
      trottle={500}
      bordered={bordered}
      className={className}
      classNameActive={isVariantBookmark ? undefined : styles["icon-toggle-in-ml-active"]}
      icon={isVariantBookmark ? (
        isBookInLibrary ? EgwWebFont["bookmark"] : EgwWebFont["bookmark-outline"]
      ) : EgwWebFont["heart-outline"]}
      title={isBookInLibrary ? t("removeFromLibrary") : t("addToLibrary")}
      active={isBookInLibrary}
      disabled={!book}
      onClick={async () => {
        logOtherEvent(OtherLogEvents.READER_ACTION, {
          id: "toggle in ML",
        });

        // if this book is for sale its data from the shop should be fetched by now
        if (!shopBook || shopBook.is_purchased) {
          const res = await toggleInML();

          if (res?.authWindowWasOpened) {
            refIsAddBookToMLOnBecameAuthorized.current = true;
          }
        } else {
          dispatch(_sharedActionsSystem.toast("@pleaseBuyThisBook"));
        }
      }}
      color={color}
      {...PROPS_CONTROL_BTN_HIDE_POPUP_ON_CLICK}
    />
  );
};
